import { useCallback, useEffect, useMemo, useState } from 'react'

import clsx from 'clsx'
import { isEmpty } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { FileDownload } from '@mui/icons-material'
import { Box, Button, createTheme, Stack, Tooltip, Typography } from '@mui/material'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { DataGridPro } from '@mui/x-data-grid-pro'

import { MobileList, MobileListCardRow } from '@common/components'
import { getValueFromColumnDef } from '@common/components/MobileList'
import { formatCurrency, useSmallScreen } from '@common/utils'

import HypotheticalRatesSelector from './components/HypotheticalRatesSelector'
import RangeSelector from './components/RangeSelector'
import { AVAILABLE_INTERVALS } from './constants'
import { calculateDateRanges } from './utils'

const hypotheticalBorderColor = '#1f2a44'
const hypotheticalBorderStyle = `1px solid ${hypotheticalBorderColor} !important`
const defaultBorderColor = '#f0f0f0'
const defaultBorderStyle = `1px solid ${defaultBorderColor}`

const isValueExist = (v) => v !== null && v !== undefined
const calculatePercentageDifference = (oldValue, newValue) =>
  ((newValue - oldValue) / Math.abs(oldValue)) * 100
const noDataPlaceholder = '--'

export default function Performance({ property }) {
  const isSmallScreen = useSmallScreen()
  const theme = useTheme()
  const gridTheme = createTheme(theme, {
    palette: {
      DataGrid: {
        pinnedBg: 'white',
        headerBg: 'white',
      },
    },
  })

  const {
    performanceData,
    performanceDataIsLoading,
    doFetchPerformanceData,
    doExportPerformanceData,
    doShowSnackbar,
  } = useConnect(
    'selectPerformanceData',
    'selectPerformanceDataIsLoading',
    'doFetchPerformanceData',
    'doExportPerformanceData',
    'doShowSnackbar',
  )

  const [pdfExporting, setPdfExporting] = useState(false)
  const [hypotheticalChargeRates, setHypotheticalChargeRates] = useState([70])
  const [selectedInterval, setSelectedInterval] = useState(AVAILABLE_INTERVALS.monthly)
  const [dateRangeValue, setDateRangeValue] = useState(null)

  const isLastRow = useCallback(
    (params) =>
      params.api.getRowIdFromRowIndex(params.api.getRowsCount() - 1) === params.row.id,
    [],
  )

  const isTotalRow = useCallback((row) => row.id === 'total_avg', [])

  useEffect(() => {
    if (property) {
      setDateRangeValue(
        calculateDateRanges(AVAILABLE_INTERVALS.monthly, property.goLiveDate),
      )
    }
  }, [property])

  useEffect(() => {
    if (dateRangeValue) {
      doFetchPerformanceData({
        property: property.id,
        propertyName: property.name,
        interval: selectedInterval.id,
        ranges: dateRangeValue,
        hypotheticalChargeRates,
      })
    }
  }, [dateRangeValue, hypotheticalChargeRates])

  const handleExport = useCallback(async () => {
    try {
      setPdfExporting(true)
      await doExportPerformanceData()
    } catch (err) {
      doShowSnackbar('An error occurred during CSV export', 'error')
    } finally {
      setPdfExporting(false)
    }
  }, [performanceData])

  const actualFields = [
    {
      display: 'flex',
      flex: 1,
      field: 'numberOfEvents',
      headerName: 'Billable Events',
      sortable: false,
      type: 'number',
      minWidth: 140,
      valueFormatter: (value) => value ?? noDataPlaceholder,
    },
    {
      display: 'flex',
      flex: 1,
      field: 'numberOfEventsCharged',
      headerName: 'Events Charged',
      sortable: false,
      type: 'number',
      minWidth: 140,
      valueFormatter: (value) => value ?? noDataPlaceholder,
    },
    {
      display: 'flex',
      flex: 1,
      field: 'chargeRate',
      headerName: 'Charge Rate',
      sortable: false,
      minWidth: 120,
      type: 'number',
      valueFormatter: (value) =>
        isValueExist(value) ? `${Number(value).toFixed(1)}%` : noDataPlaceholder,
    },
    {
      display: 'flex',
      flex: 1,
      field: 'netCharges',
      headerName: 'Net Charges',
      sortable: false,
      minWidth: 130,
      type: 'number',
      valueFormatter: (value) =>
        isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
    },
    {
      display: 'flex',
      flex: 1,
      field: 'projectedRestCosts',
      headerName: 'Rest Costs',
      sortable: false,
      minWidth: 130,
      type: 'number',
      valueFormatter: (value) =>
        isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
    },
    {
      display: 'flex',
      flex: 1,
      field: 'netProfit',
      headerName: 'Net Profit',
      sortable: false,
      minWidth: 130,
      type: 'number',
      valueFormatter: (value) =>
        isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
    },
  ].map((column) => ({
    ...column,
    ...(!isSmallScreen.mobile
      ? {
          renderCell: ({ row, colDef }) =>
            isTotalRow(row) ? (
              <Typography variant="body2" fontSize={12} color="grey.700">
                {colDef?.valueFormatter(row[column.field])}
              </Typography>
            ) : undefined,
        }
      : {}),
  }))

  const getDiffData = ({ actualValue, hypotheticalValue }) => {
    const percentage = calculatePercentageDifference(actualValue, hypotheticalValue)
    let color = 'teal'
    let icon = ''

    if (percentage > 0) {
      icon = '↑'
      color = 'green'
    } else if (percentage < 0) {
      icon = '↓'
      color = 'red'
    }

    return {
      percentage: Number.isNaN(percentage) ? null : percentage.toFixed(1),
      icon,
      color,
    }
  }

  const buildHypotheticalFields = ({ rate, index, length }) => {
    const buildCellClassName = (params, field) =>
      field.cellClassName ? `${field.cellClassName(params)}` : `hypothetical-col`
    const buildHeaderClassName = (field) => {
      const prefix = field.headerClassPrefixBuilder
        ? field.headerClassPrefixBuilder()
        : null
      return prefix ? `${prefix}-hypothetical-header-cell` : `hypothetical-header-cell`
    }

    let className = 'hypothetical-col'
    let prefix = null
    if (length > 1 && index === 0) {
      prefix = 'first'
      className = 'hypothetical-first-col'
    } else if (length > 1 && index === length - 1) {
      prefix = 'last'
      className = 'hypothetical-last-col'
    }

    const fields = [
      {
        field: `hypothetical-${rate}-netProfit`,
        headerName: 'Net Profit',
        minWidth: 184,
        cellClassName: (params) =>
          clsx(className, {
            last: isLastRow(params),
          }),
        headerClassPrefixBuilder: prefix ? () => prefix : null,
        valueGetter: (_, row) =>
          row.projectedResults.find((item) => item.chargeRate === rate.toFixed(1))
            ?.netProfit,
        valueFormatter: (value) =>
          isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
        renderCell: ({ row, colDef }) => {
          const actualValue = row.netProfit
          const hypotheticalValue = colDef?.valueGetter(null, row)
          const formattedValue = colDef?.valueFormatter(hypotheticalValue)
          const { percentage, color, icon } = getDiffData({
            actualValue,
            hypotheticalValue,
          })

          const data = row.projectedResults.find(
            (item) => item.chargeRate === rate.toFixed(1),
          )
          const eventCharged = data?.numberOfEventsCharged
          const netCharges = data?.netCharges
          const projectedRestCosts = data?.projectedRestCosts
          const tooltipData = [
            `Events Charged: ${eventCharged ?? noDataPlaceholder}`,
            `Net Charges: ${
              isValueExist(netCharges) ? formatCurrency(netCharges) : noDataPlaceholder
            }`,
            `Rest Costs: ${
              isValueExist(projectedRestCosts)
                ? formatCurrency(projectedRestCosts)
                : noDataPlaceholder
            }`,
          ]

          return (
            <Tooltip
              disableInteractive
              title={
                <div style={{ whiteSpace: 'pre-line' }}>{tooltipData.join('\n')}</div>
              }
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{ opacity: isTotalRow(row) ? 0.7 : 1 }}
              >
                <Typography variant="body2" fontSize={isTotalRow(row) ? 12 : 14}>
                  {formattedValue}
                </Typography>
                <Typography
                  variant="body2"
                  fontSize={isTotalRow(row) ? 10 : 12}
                  color={color}
                >
                  {`(${icon}${`${
                    percentage !== null ? `${percentage}%` : noDataPlaceholder
                  }`})`}
                </Typography>
              </Box>
            </Tooltip>
          )
        },
      },
      ...(isSmallScreen.mobile
        ? [
            {
              field: `hypothetical-${rate}-numberOfEventsCharged`,
              headerName: 'Events Charged',
              valueGetter: (_, row) =>
                row.projectedResults.find((item) => item.chargeRate === rate.toFixed(1))
                  ?.numberOfEventsCharged,
              valueFormatter: (value) => value ?? noDataPlaceholder,
            },
            {
              field: `hypothetical-${rate}-netCharges`,
              headerName: 'Net Charges',
              valueGetter: (_, row) =>
                row.projectedResults.find((item) => item.chargeRate === rate.toFixed(1))
                  ?.netCharges,
              valueFormatter: (value) =>
                isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
            },
            {
              field: `hypothetical-${rate}-projectedRestCosts`,
              headerName: 'Rest Costs',
              valueGetter: (_, row) =>
                row.projectedResults.find((item) => item.chargeRate === rate.toFixed(1))
                  ?.projectedRestCosts,
              valueFormatter: (value) =>
                isValueExist(value) ? formatCurrency(value) : noDataPlaceholder,
            },
          ]
        : []),
    ]

    return fields.map((field) => ({
      display: 'flex',
      flex: 1,
      field: field.field,
      headerName: field.headerName,
      sortable: false,
      type: 'number',
      minWidth: field.minWidth,
      valueGetter: field.valueGetter,
      valueFormatter: field.valueFormatter,
      renderCell: field.renderCell,
      cellClassName: (params) => buildCellClassName(params, field),
      headerClassName: buildHeaderClassName(field),
    }))
  }

  const hypotheticalFields = useMemo(
    () =>
      hypotheticalChargeRates.reduce(
        (acc, rate, index) => ({
          ...acc,
          [rate]: buildHypotheticalFields({
            rate,
            index,
            length: hypotheticalChargeRates.length,
          }),
        }),
        {},
      ),
    [hypotheticalChargeRates, isSmallScreen],
  )

  const columns = [
    {
      display: 'flex',
      flex: selectedInterval.id === AVAILABLE_INTERVALS.monthly.id ? 0.5 : 1.2,
      field: 'startDate',
      headerName: 'Date Range',
      sortable: false,
      minWidth: selectedInterval.id === AVAILABLE_INTERVALS.monthly.id ? 140 : 194,
      valueGetter: (_, row) => {
        if (isTotalRow(row)) return 'Total / Average'

        switch (selectedInterval.id) {
          case AVAILABLE_INTERVALS.monthly.id:
            return row.startDate.toFormat('MMM yyyy')
          default: {
            const sameYear = row.startDate.year === row.endDate.year
            const sameMonth = sameYear && row.startDate.month === row.endDate.month

            if (sameMonth) {
              const firstPart = row.startDate.toFormat('MMM d-')
              const secondPart = row.endDate.toFormat('d yyyy')
              return `${firstPart}${secondPart}`
            }
            const formattedStart = row.startDate.toFormat('MMM d yyyy')
            const formattedEnd = row.endDate.toFormat('MMM d yyyy')
            return `${formattedStart} - ${formattedEnd}`
          }
        }
      },
      renderCell: ({ row, colDef }) => {
        if (isTotalRow(row)) {
          return (
            <Typography variant="body2" fontSize={12} color="grey.700">
              {colDef?.valueGetter(null, row)}
            </Typography>
          )
        }

        const formattedStart = row.startDate.toFormat('MMM d yyyy')
        const formattedEnd = row.endDate.toFormat('MMM d yyyy')
        return (
          <Tooltip disableInteractive title={`${formattedStart} - ${formattedEnd}`}>
            <Typography variant="body2">{colDef?.valueGetter(null, row)}</Typography>
          </Tooltip>
        )
      },
    },
    ...actualFields,
    ...Object.values(hypotheticalFields).reduce((acc, field) => [...acc, ...field], []),
  ]

  const MobileListItem = useCallback(
    (row) => {
      const hypotheticalDataFields = columns
        .filter((column) => column.field.startsWith('hypothetical'))
        .map((column) => column.field)

      const ignoredFields = ['startDate', ...hypotheticalDataFields]
      const filteredColumns = columns.filter(
        (column) => !ignoredFields.includes(column.field),
      )

      const rangeCol = columns.find((col) => col.field === 'startDate')
      const range = rangeCol?.valueGetter(null, row)

      const periodBoxStyle = { border: 1, borderRadius: 1.5, p: 1 }

      return (
        <Stack spacing={0.5}>
          <Box display="flex" alignItems="center" sx={{ '&&': { mb: 1 } }}>
            {range}
          </Box>
          {filteredColumns.map((column) => {
            const value = getValueFromColumnDef({ row, column })
            return (
              <MobileListCardRow
                key={column.field}
                label={column.headerName}
                value={value}
              />
            )
          })}
          <Box>
            {hypotheticalChargeRates.map((rate) => (
              <Box
                display="flex"
                flexDirection="column"
                gap={1}
                sx={{ '&&': { mt: 2 }, ...periodBoxStyle }}
              >
                <Typography variant="caption" fontStyle="italic">
                  Charge Rate {rate}%
                </Typography>
                <Box>
                  {columns
                    .filter((column) => column.field.startsWith(`hypothetical-${rate}`))
                    .map((column) => {
                      const hypotheticalValue = column.valueGetter(null, row)
                      const formattedValue = column.valueFormatter(hypotheticalValue)

                      let diffData
                      if (column.field.endsWith('netProfit')) {
                        const actualValue = row.netProfit
                        diffData = getDiffData({ actualValue, hypotheticalValue })
                      }

                      return (
                        <MobileListCardRow
                          key={column.field}
                          label={column.headerName}
                          value={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption">
                                {formattedValue}
                              </Typography>
                              {diffData && (
                                <Typography
                                  variant="caption"
                                  color={diffData.color}
                                >{`(${diffData.icon}${
                                  !diffData.percentage !== null
                                    ? `${diffData.percentage}%`
                                    : noDataPlaceholder
                                })`}</Typography>
                              )}
                            </Box>
                          }
                        />
                      )
                    })}
                </Box>
              </Box>
            ))}
          </Box>
        </Stack>
      )
    },
    [columns],
  )

  const rows = useMemo(() => {
    if (performanceData) {
      return [
        ...performanceData.data,
        ...(performanceData.totals ? [performanceData.totals] : []),
      ]
    }
    return []
  }, [performanceData])

  return (
    <Box>
      <Box
        display="flex"
        flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
        alignItems={isSmallScreen.mobile ? 'center' : 'end'}
        gap={2}
        justifyContent="space-between"
      >
        <Box
          display="flex"
          flexDirection={isSmallScreen.mobile ? 'column' : 'row'}
          alignItems="center"
          gap={isSmallScreen.mobile ? 1 : 2}
          width={isSmallScreen.mobile ? 1 : null}
        >
          <RangeSelector
            selectedInterval={selectedInterval}
            dateRangeValue={dateRangeValue}
            goLiveDate={property?.goLiveDate}
            disabled={performanceDataIsLoading || pdfExporting}
            onChange={(interval) => {
              setSelectedInterval(interval)
              setDateRangeValue(interval.ranges)
            }}
          />
          <HypotheticalRatesSelector
            disabled={performanceDataIsLoading || pdfExporting}
            values={hypotheticalChargeRates}
            onChange={(operation, value) => {
              if (operation === 'add') {
                setHypotheticalChargeRates((prev) => [...prev, value].sort())
              } else if (operation === 'delete') {
                setHypotheticalChargeRates((prev) => {
                  const updatedValues = [...prev]
                  const indexToRemove = prev.indexOf(value)
                  if (indexToRemove !== -1) {
                    updatedValues.splice(indexToRemove, 1)
                  }
                  return updatedValues.sort()
                })
              }
            }}
          />
        </Box>
        <Button
          fullWidth={isSmallScreen.mobile}
          variant="outlined"
          disabled={performanceDataIsLoading || pdfExporting || !rows || isEmpty(rows)}
          startIcon={<FileDownload />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Box>
      {isSmallScreen.mobile ? (
        <MobileList
          hideFooter
          title="Dashboard"
          loading={performanceDataIsLoading}
          itemBuilder={MobileListItem}
          page={1}
          pageSize={9999}
          rows={rows}
          rowCount={rows.length}
        />
      ) : (
        <ThemeProvider theme={gridTheme}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              height: 'calc(100vh - 478px)',
              mt: 2,
              '& .MuiDataGrid-root, .MuiDataGrid-footerContainer, .MuiDataGrid-columnHeaders':
                { border: 'none' },
              '& .MuiDataGrid-root': {
                '--DataGrid-rowBorderColor': 'transparent !important',
              },
              '& .hypothetical-header-cell': {
                borderLeft: hypotheticalBorderStyle,
                borderRight:
                  hypotheticalChargeRates.length === 1 ? hypotheticalBorderStyle : null,
              },
              '& .first-hypothetical-header-cell': {
                borderLeft: hypotheticalBorderStyle,
              },
              '& .last-hypothetical-header-cell': {
                borderLeft: hypotheticalBorderStyle,
                borderRight: hypotheticalBorderStyle,
              },
              '& .hypothetical-col': {
                borderLeft: hypotheticalBorderStyle,
                borderRight:
                  hypotheticalChargeRates.length === 1 ? hypotheticalBorderStyle : null,
              },
              '& .hypothetical-first-col': { borderLeft: hypotheticalBorderStyle },
              '& .hypothetical-first-col.last, & .hypothetical-last-col.last, & .hypothetical-col.last':
                {
                  borderBottom: hypotheticalBorderStyle,
                  borderBottomColor: `${hypotheticalBorderColor} !important`,
                },
              '& .hypothetical-last-col': {
                borderLeft: hypotheticalBorderStyle,
                borderRight: hypotheticalBorderStyle,
              },
              '& .hypothetical-header-group': {
                borderTop: hypotheticalBorderStyle,
                borderLeft: hypotheticalBorderStyle,
              },
              '& .hypothetical-first-header-group': {
                borderTop: hypotheticalBorderStyle,
                borderLeft: hypotheticalBorderStyle,
                borderRight:
                  hypotheticalChargeRates.length === 1 ? hypotheticalBorderStyle : null,
              },
              '& .hypothetical-last-header-group': {
                borderTop: hypotheticalBorderStyle,
                borderLeft: hypotheticalBorderStyle,
                borderRight: hypotheticalBorderStyle,
              },
            }}
          >
            <DataGridPro
              disableRowSelectionOnClick
              disableColumnFilter
              disableColumnMenu
              disableColumnResize
              hideFooter
              columnHeaderHeight={32}
              rowHeight={20}
              initialState={{ pinnedColumns: { left: ['startDate'] } }}
              columnGroupingModel={[
                {
                  groupId: 'actual',
                  headerName: 'Actual',
                  headerAlign: 'center',
                  headerClassName: 'actual-header-group',
                  children: actualFields.map((col) => ({ field: col.field })),
                },
                ...hypotheticalChargeRates.map((rate, index) => ({
                  groupId: `hypothetical-${rate}`,
                  headerName: `${rate}% Charge Rate`,
                  headerAlign: 'center',
                  headerClassName:
                    index > 0 && index < hypotheticalChargeRates.length - 1
                      ? 'hypothetical-header-group'
                      : `hypothetical-${index === 0 ? 'first' : 'last'}-header-group`,
                  children: hypotheticalFields[rate].map((col) => ({
                    field: col.field,
                  })),
                })),
              ]}
              columns={columns}
              loading={performanceDataIsLoading}
              density="comfortable"
              rows={rows}
              sx={{
                '& .MuiDataGrid-columnHeader': {
                  borderTop: defaultBorderStyle,
                  borderBottom: 'none',
                },
                '& .MuiDataGrid-columnHeader--last': {
                  borderRight: defaultBorderStyle,
                },
                '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
                  borderLeft: defaultBorderStyle,
                },
                '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
                  borderBottom: defaultBorderStyle,
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                  marginBottom: 1,
                  marginRight: 1,
                },
                '& .MuiDataGrid-container--top:after': { height: 0 },
                '& .MuiDataGrid-columnSeparator': { display: 'none' },
                '& .MuiDataGrid-filler--pinnedLeft': { borderRight: 'none' },
                '& .MuiDataGrid-row': { '--rowBorderColor': 'transparent' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell--withRightBorder, & .MuiDataGrid-columnHeader--withRightBorder':
                  {
                    borderRight: 'none',
                    boxShadow: `0.5px 0px 0px ${defaultBorderColor}`,
                  },
                '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                '& .MuiDataGrid-pinnedColumnHeaders, & .MuiDataGrid-pinnedColumns': {
                  boxShadow: `0.5px 0px 0px ${defaultBorderColor}`,
                },
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  borderBottomWidth: '0px !important',
                },
                '& .clickableCell': (themeObj) => ({
                  cursor: 'pointer',
                  color: 'primary.main',
                  ...themeObj.typography.subtitle2,
                }),
              }}
            />
          </Box>
        </ThemeProvider>
      )}
    </Box>
  )
}
